import { createClient } from './supabase';
import type { Product, ProductStatistics } from '@/types/business';
import { handleError } from './error-handler';

export class ProductService {
  // Get all products with optional filters
  async getAll(filters: {
    business_id?: string;
    status?: 'available' | 'reserved' | 'sold' | 'expired';
    category?: string;
  } = {}): Promise<Product[]> {
    try {
      const supabase = await createClient();
      
      // Build query with filters
      let query = supabase.from('products').select('*');
      
      // Apply filters directly in the query
      if (filters.business_id) {
        query = query.eq('business_id', filters.business_id);
      }
      
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      
      if (filters.category) {
        query = query.eq('category', filters.category);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as Product[];
    } catch (error) {
      console.error('Error fetching products:', error);
      return handleError(error);
    }
  }

  // Get a specific product by ID
  async getById(productId: string): Promise<Product> {
    try {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', productId)
        .single();
      
      if (error) throw error;
      return data as Product;
    } catch (error) {
      console.error(`Error fetching product ${productId}:`, error);
      return handleError(error);
    }
  }

  // Create a new product
  async create(productData: Omit<Product, 'id' | 'created_at' | 'updated_at' | 'status'>): Promise<Product> {
    try {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('products')
        .insert({
          ...productData,
          status: 'available' as const
        })
        .select()
        .single();
      
      if (error) throw error;
      return data as Product;
    } catch (error) {
      console.error('Error creating product:', error);
      return handleError(error);
    }
  }

  // Update a product
  async update(
    productId: string,
    productData: Partial<Omit<Product, 'id' | 'business_id' | 'created_at' | 'updated_at'>>
  ): Promise<Product> {
    try {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('products')
        .update(productData)
        .eq('id', productId)
        .select()
        .single();
      
      if (error) throw error;
      return data as Product;
    } catch (error) {
      console.error(`Error updating product ${productId}:`, error);
      return handleError(error);
    }
  }

  // Delete a product
  async delete(productId: string): Promise<void> {
    try {
      const supabase = await createClient();
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);
      
      if (error) throw error;
    } catch (error) {
      console.error(`Error deleting product ${productId}:`, error);
      return handleError(error);
    }
  }

  // Get product statistics
  async getStatistics(businessId: string): Promise<ProductStatistics> {
    try {
      const supabase = await createClient();
      
      // Call Supabase function to get product statistics
      const { data, error } = await supabase
        .rpc('get_product_statistics', { business_id: businessId });
      
      if (error) throw error;
      return data as ProductStatistics;
    } catch (error) {
      console.error(`Error fetching product statistics for business ${businessId}:`, error);
      return handleError(error);
    }
  }
}

export const productService = new ProductService();
